import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import type { BracketColumn, BracketMatch, BracketShape } from '../types'

export const BRACKET_WINNER_COLUMN_CODE = 'winner'
export const BRACKET_GF_COLUMN_CODE = 'gf'

export const BRACKET_UNIT_EM = 13.2
/** Вертикальный шаг строк в lower-секции GSL (компактнее upper). */
export const BRACKET_LOWER_ROW_UNIT_EM = 10
export const BRACKET_MATCH_WIDTH_EM = 22
export const BRACKET_COLUMN_GAP_EM = 5
export const BRACKET_MATCH_HEIGHT_EM = 9.2
export const BRACKET_SLOT_HEIGHT_EM = BRACKET_MATCH_HEIGHT_EM / 2
export const BRACKET_SECTION_LABEL_EM = 2
export const BRACKET_SECTION_GAP_EM = 3
export const BRACKET_COLUMN_HEADER_EM = 2.4

export const GSL_LOWER_COLUMN_CODES = new Set(['lr1', 'lr', 'lr2', 'ls', 'lf'])

/** Колонка матча за 3-е место — не часть основной SE-сетки. */
const BRACKET_THIRD_PLACE_COLUMN_CODE = 'tpm'

export type BracketLayoutMode = 'linear' | 'gsl-dual-track'
export type BracketConnectorKind = 'bracket' | 'drop'

/** Вертикальный центр слота внутри карточки матча (top матча = 0). */
export const bracketSlotCenterY = (
  matchTopY: number,
  side: 'left' | 'right'
): number =>
  matchTopY +
  (side === 'left'
    ? BRACKET_SLOT_HEIGHT_EM / 2
    : BRACKET_SLOT_HEIGHT_EM + BRACKET_SLOT_HEIGHT_EM / 2)

export interface BracketLayoutMatch {
  match: BracketMatch
  columnCode: string
  columnIndex: number
  row: number
  x: number
  y: number
}

export interface BracketLayoutConnector {
  from: { x: number; y: number; matchId: number }
  to: { x: number; y: number; matchId: number }
  side: 'left' | 'right'
  kind: BracketConnectorKind
}

export interface BracketLayoutWinner {
  x: number
  y: number
  matchId: number
}

export interface BracketLayoutThirdPlace {
  matchId: number
  label: string
}

export interface BracketLayoutSection {
  id: 'upper' | 'lower'
  labelKey: 'upperBracket' | 'lowerBracket'
  top: number
  columnHeaderTop: number
  columns: { code: string; label: string; x: number; width: number }[]
  height: number
}

export interface BracketLayout {
  mode: BracketLayoutMode
  matches: Map<number, BracketLayoutMatch>
  connectors: BracketLayoutConnector[]
  width: number
  height: number
  columns: { code: string; label: string; x: number; width: number }[]
  sections: BracketLayoutSection[]
  winner: BracketLayoutWinner | null
  thirdPlace: BracketLayoutThirdPlace | null
  thirdPlaceWinner: BracketLayoutWinner | null
}

const buildEmptyLayout = (): BracketLayout => ({
  mode: 'linear',
  matches: new Map(),
  connectors: [],
  width: 0,
  height: 0,
  columns: [],
  sections: [],
  winner: null,
  thirdPlace: null,
  thirdPlaceWinner: null
})

export const isGslDualTrackBracket = (bracket: BracketShape): boolean =>
  bracket.columns.some(column => GSL_LOWER_COLUMN_CODES.has(column.code))

const isLowerTrackMatch = (item: BracketLayoutMatch) =>
  GSL_LOWER_COLUMN_CODES.has(item.columnCode)

const getThirdPlaceMatch = (bracket: BracketShape): BracketMatch | undefined =>
  bracket.has_third_place
    ? bracket.matches.find(
        match => match.column_code === BRACKET_THIRD_PLACE_COLUMN_CODE
      )
    : undefined

const groupMatchesByColumn = (matches: BracketMatch[]) => {
  const matchesByColumn = new Map<string, BracketMatch[]>()

  for (const match of matches) {
    if (!matchesByColumn.has(match.column_code)) {
      matchesByColumn.set(match.column_code, [])
    }
    matchesByColumn.get(match.column_code)!.push(match)
  }

  for (const arr of matchesByColumn.values()) {
    arr.sort((a, b) => a.position - b.position)
  }

  return matchesByColumn
}

/** Single-elim и прочие линейные пресеты. */
export const resolveBracketMatchRow = (
  match: BracketMatch,
  placed: ReadonlyMap<number, BracketLayoutMatch>,
  idxInColumn: number
): number => {
  const sourcePlaced: BracketLayoutMatch[] = []

  for (const slot of match.slots) {
    if (!slot.source_match_id) continue
    const source = placed.get(slot.source_match_id)
    if (source) sourcePlaced.push(source)
  }

  if (!sourcePlaced.length) return idxInColumn

  const minColumnIndex = Math.min(...sourcePlaced.map(s => s.columnIndex))
  const primaryRows = sourcePlaced
    .filter(s => s.columnIndex === minColumnIndex)
    .map(s => s.row)

  return primaryRows.reduce((sum, row) => sum + row, 0) / primaryRows.length
}

/** Нижняя GSL-сетка: ряд карточки берём только от предыдущего матча снизу.
 *  Матч сверху (проигравший upper) на высоту не влияет — иначе карточка
 *  уехала бы к верхней сетке, и линия от lower шла бы наискосок. */
const resolveGslLowerTrackRow = (
  match: BracketMatch,
  placed: ReadonlyMap<number, BracketLayoutMatch>,
  idxInColumn: number
): number => {
  const sourceRows: number[] = []

  for (const slot of match.slots) {
    if (!slot.source_match_id) continue
    const source = placed.get(slot.source_match_id)
    if (!source || !isLowerTrackMatch(source)) continue
    sourceRows.push(source.row)
  }

  if (!sourceRows.length) return idxInColumn
  if (sourceRows.length === 1) return sourceRows[0]

  return sourceRows.reduce((sum, row) => sum + row, 0) / sourceRows.length
}

const columnWidthAt = (columnIndex: number) =>
  columnIndex * (BRACKET_MATCH_WIDTH_EM + BRACKET_COLUMN_GAP_EM)

const trackWidth = (columnCount: number) =>
  columnCount * BRACKET_MATCH_WIDTH_EM +
  Math.max(columnCount - 1, 0) * BRACKET_COLUMN_GAP_EM

const placeTrackColumns = ({
  columns,
  matchesByColumn,
  placed,
  matchesTop,
  isLowerTrack
}: {
  columns: BracketColumn[]
  matchesByColumn: Map<string, BracketMatch[]>
  placed: Map<number, BracketLayoutMatch>
  matchesTop: number
  isLowerTrack: boolean
}) => {
  let maxRow = 0
  const rowUnit = isLowerTrack ? BRACKET_LOWER_ROW_UNIT_EM : BRACKET_UNIT_EM

  columns.forEach((column, columnIndex) => {
    const matches = matchesByColumn.get(column.code) ?? []

    matches.forEach((match, idxInColumn) => {
      const row = isLowerTrack
        ? resolveGslLowerTrackRow(match, placed, idxInColumn)
        : resolveBracketMatchRow(match, placed, idxInColumn)

      const x = columnWidthAt(columnIndex)
      const y = matchesTop + row * rowUnit

      placed.set(match.id, {
        match,
        columnCode: column.code,
        columnIndex,
        row,
        x,
        y
      })

      if (row > maxRow) maxRow = row
    })
  })

  return (maxRow + 1) * rowUnit
}

const buildConnectors = (
  placed: Map<number, BracketLayoutMatch>
): BracketLayoutConnector[] => {
  const connectors: BracketLayoutConnector[] = []

  for (const target of placed.values()) {
    for (const slot of target.match.slots) {
      if (!slot.source_match_id) continue

      const source = placed.get(slot.source_match_id)
      if (!source) continue

      const isDrop =
        slot.fill_rule === 'loser_of' &&
        !GSL_LOWER_COLUMN_CODES.has(source.columnCode) &&
        GSL_LOWER_COLUMN_CODES.has(target.columnCode)

      const sourceRightX = source.x + BRACKET_MATCH_WIDTH_EM
      const sourceCenterY = source.y + BRACKET_MATCH_HEIGHT_EM / 2
      const targetLeftX = target.x
      const targetSlotCenterY = bracketSlotCenterY(target.y, slot.side)

      connectors.push({
        from: isDrop
          ? {
              x: source.x + BRACKET_MATCH_WIDTH_EM / 2,
              y: source.y + BRACKET_MATCH_HEIGHT_EM,
              matchId: source.match.id
            }
          : {
              x: sourceRightX,
              y: sourceCenterY,
              matchId: source.match.id
            },
        to: isDrop
          ? {
              x: target.x + BRACKET_MATCH_WIDTH_EM / 2,
              y: target.y,
              matchId: target.match.id
            }
          : {
              x: targetLeftX,
              y: targetSlotCenterY,
              matchId: target.match.id
            },
        side: slot.side,
        kind: isDrop ? 'drop' : 'bracket'
      })
    }
  }

  return connectors
}

/** GSL: рисуем линии только внутри верхней сетки и только внутри нижней.
 *  Линию «проигравший сверху → матч снизу» не рисуем. */
const buildGslTrackConnectors = (
  placed: Map<number, BracketLayoutMatch>
): BracketLayoutConnector[] =>
  buildConnectors(placed).filter(connector => {
    if (connector.kind === 'drop') return false

    const source = placed.get(connector.from.matchId)
    const target = placed.get(connector.to.matchId)
    if (!source || !target) return false

    return isLowerTrackMatch(source) === isLowerTrackMatch(target)
  })

const getGrandFinalPlaced = (
  placed: Map<number, BracketLayoutMatch>
): BracketLayoutMatch | undefined =>
  [...placed.values()].find(item => item.columnCode === BRACKET_GF_COLUMN_CODE)

const buildWinnerConnector = (
  fromPlaced: BracketLayoutMatch,
  winner: BracketLayoutWinner
): BracketLayoutConnector => {
  const centerY = fromPlaced.y + BRACKET_MATCH_HEIGHT_EM / 2

  return {
    from: {
      x: fromPlaced.x + BRACKET_MATCH_WIDTH_EM,
      y: centerY,
      matchId: fromPlaced.match.id
    },
    to: {
      x: winner.x,
      y: centerY,
      matchId: fromPlaced.match.id
    },
    side: 'left',
    kind: 'bracket'
  }
}

const buildWinner = (
  gfPlaced: BracketLayoutMatch | undefined,
  columnCount: number
): {
  winner: BracketLayoutWinner | null
  extraConnector: BracketLayoutConnector | null
} => {
  if (!gfPlaced) {
    return { winner: null, extraConnector: null }
  }

  const gfCenterY = gfPlaced.y + BRACKET_MATCH_HEIGHT_EM / 2
  const winnerX = columnCount * (BRACKET_MATCH_WIDTH_EM + BRACKET_COLUMN_GAP_EM)
  const winnerY = gfCenterY - BRACKET_SLOT_HEIGHT_EM / 2
  const winner: BracketLayoutWinner = {
    x: winnerX,
    y: winnerY,
    matchId: gfPlaced.match.id
  }

  return {
    winner,
    extraConnector: buildWinnerConnector(gfPlaced, winner)
  }
}

const attachThirdPlace = ({
  match,
  label,
  gfPlaced,
  placed,
  connectors,
  winnerColumnX,
  height
}: {
  match: BracketMatch | undefined
  label: string
  gfPlaced: BracketLayoutMatch | undefined
  placed: Map<number, BracketLayoutMatch>
  connectors: BracketLayoutConnector[]
  winnerColumnX: number
  height: number
}): {
  thirdPlace: BracketLayoutThirdPlace | null
  thirdPlaceWinner: BracketLayoutWinner | null
  height: number
} => {
  if (!match || !gfPlaced) {
    return { thirdPlace: null, thirdPlaceWinner: null, height }
  }

  const y = gfPlaced.y + BRACKET_UNIT_EM + BRACKET_COLUMN_HEADER_EM
  const x = gfPlaced.x

  const thirdPlacePlaced: BracketLayoutMatch = {
    match,
    columnCode: match.column_code,
    columnIndex: gfPlaced.columnIndex,
    row: gfPlaced.row + 1,
    x,
    y
  }
  placed.set(match.id, thirdPlacePlaced)

  const centerY = y + BRACKET_MATCH_HEIGHT_EM / 2
  const thirdPlaceWinner: BracketLayoutWinner = {
    x: winnerColumnX,
    y: centerY - BRACKET_SLOT_HEIGHT_EM / 2,
    matchId: match.id
  }

  connectors.push(buildWinnerConnector(thirdPlacePlaced, thirdPlaceWinner))

  return {
    thirdPlace: {
      matchId: match.id,
      label
    },
    thirdPlaceWinner,
    height: Math.max(height, y + BRACKET_UNIT_EM)
  }
}

const computeLinearLayout = (bracket: BracketShape): BracketLayout => {
  const thirdPlaceMatch = getThirdPlaceMatch(bracket)
  const matchesByColumn = groupMatchesByColumn(bracket.matches)
  const placed = new Map<number, BracketLayoutMatch>()
  const orderedColumns = bracket.has_third_place
    ? bracket.columns.filter(
        column => column.code !== BRACKET_THIRD_PLACE_COLUMN_CODE
      )
    : bracket.columns
  let maxRow = 0

  orderedColumns.forEach((column, columnIndex) => {
    const matches = matchesByColumn.get(column.code) ?? []

    matches.forEach((match, idxInColumn) => {
      const row = resolveBracketMatchRow(match, placed, idxInColumn)
      const x = columnWidthAt(columnIndex)
      const y = row * BRACKET_UNIT_EM

      placed.set(match.id, {
        match,
        columnCode: column.code,
        columnIndex,
        row,
        x,
        y
      })

      if (row > maxRow) maxRow = row
    })
  })

  const connectors = buildConnectors(placed)
  const gfPlaced = getGrandFinalPlaced(placed)
  const { winner, extraConnector } = buildWinner(
    gfPlaced,
    orderedColumns.length
  )

  if (extraConnector) connectors.push(extraConnector)

  const matchColumnCount = orderedColumns.length + (gfPlaced ? 1 : 0)
  const columns = orderedColumns.map((column, idx) => ({
    code: column.code,
    label: column.label,
    x: columnWidthAt(idx),
    width: BRACKET_MATCH_WIDTH_EM
  }))

  if (winner) {
    columns.push({
      code: BRACKET_WINNER_COLUMN_CODE,
      label: '',
      x: winner.x,
      width: BRACKET_MATCH_WIDTH_EM
    })
  }

  const thirdPlaceLabel =
    bracket.columns.find(
      column => column.code === BRACKET_THIRD_PLACE_COLUMN_CODE
    )?.label ?? ''

  const thirdPlaceLayout = attachThirdPlace({
    match: thirdPlaceMatch,
    label: thirdPlaceLabel,
    gfPlaced,
    placed,
    connectors,
    winnerColumnX: winner?.x ?? columnWidthAt(orderedColumns.length),
    height: (maxRow + 1) * BRACKET_UNIT_EM
  })

  return {
    mode: 'linear',
    matches: placed,
    connectors,
    width: trackWidth(matchColumnCount),
    height: thirdPlaceLayout.height,
    columns,
    sections: [],
    winner,
    thirdPlace: thirdPlaceLayout.thirdPlace,
    thirdPlaceWinner: thirdPlaceLayout.thirdPlaceWinner
  }
}

const computeGslDualTrackLayout = (bracket: BracketShape): BracketLayout => {
  const upperColumns = bracket.columns.filter(
    column => !GSL_LOWER_COLUMN_CODES.has(column.code)
  )
  const lowerColumns = bracket.columns.filter(column =>
    GSL_LOWER_COLUMN_CODES.has(column.code)
  )
  const matchesByColumn = groupMatchesByColumn(bracket.matches)
  const placed = new Map<number, BracketLayoutMatch>()

  const upperSectionTop = 0
  const upperColumnHeaderTop = upperSectionTop + BRACKET_SECTION_LABEL_EM
  const upperMatchesTop = upperColumnHeaderTop + BRACKET_COLUMN_HEADER_EM

  const upperMatchesHeight = placeTrackColumns({
    columns: upperColumns,
    matchesByColumn,
    placed,
    matchesTop: upperMatchesTop,
    isLowerTrack: false
  })

  const upperSectionHeight = upperMatchesTop + upperMatchesHeight

  const lowerSectionTop = upperSectionHeight + BRACKET_SECTION_GAP_EM
  const lowerColumnHeaderTop = lowerSectionTop + BRACKET_SECTION_LABEL_EM
  const lowerMatchesTop = lowerColumnHeaderTop + BRACKET_COLUMN_HEADER_EM

  const lowerMatchesHeight = placeTrackColumns({
    columns: lowerColumns,
    matchesByColumn,
    placed,
    matchesTop: lowerMatchesTop,
    isLowerTrack: true
  })

  const lowerSectionHeight =
    lowerMatchesTop + lowerMatchesHeight - lowerSectionTop

  const columnCount = Math.max(upperColumns.length, lowerColumns.length)
  const connectors = buildGslTrackConnectors(placed)
  const { winner, extraConnector } = buildWinner(
    getGrandFinalPlaced(placed),
    columnCount
  )

  if (extraConnector) connectors.push(extraConnector)

  const mapSectionColumns = (columns: BracketColumn[]) =>
    columns.map((column, idx) => ({
      code: column.code,
      label: column.label,
      x: columnWidthAt(idx),
      width: BRACKET_MATCH_WIDTH_EM
    }))

  const sections: BracketLayoutSection[] = [
    {
      id: 'upper',
      labelKey: 'upperBracket',
      top: upperSectionTop,
      columnHeaderTop: upperColumnHeaderTop,
      columns: mapSectionColumns(upperColumns),
      height: upperSectionHeight
    },
    {
      id: 'lower',
      labelKey: 'lowerBracket',
      top: lowerSectionTop,
      columnHeaderTop: lowerColumnHeaderTop,
      columns: mapSectionColumns(lowerColumns),
      height: lowerSectionHeight
    }
  ]

  const columns = mapSectionColumns(upperColumns)

  if (winner) {
    columns.push({
      code: BRACKET_WINNER_COLUMN_CODE,
      label: '',
      x: winner.x,
      width: BRACKET_MATCH_WIDTH_EM
    })
  }

  return {
    mode: 'gsl-dual-track',
    matches: placed,
    connectors,
    width: trackWidth(columnCount + (winner ? 1 : 0)),
    height: lowerSectionTop + lowerSectionHeight,
    columns,
    sections,
    winner,
    thirdPlace: null,
    thirdPlaceWinner: null
  }
}

export const useBracketLayout = (
  bracket: MaybeRefOrGetter<BracketShape | null | undefined>
) => {
  const layout = computed<BracketLayout>(() => {
    const value = toValue(bracket)
    if (!value || !value.columns.length) return buildEmptyLayout()

    if (isGslDualTrackBracket(value)) {
      return computeGslDualTrackLayout(value)
    }

    return computeLinearLayout(value)
  })

  return { layout }
}
